import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "lib/api/api";
import AdminNavbar from "./AdminNavbar";

const AdminPaymentTrackingPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [manual, manualRequests, online] = await Promise.all([
        api.get("/admin/payments/bank-transfer"),
        api.get("/admin/payments/manual-requests"),
        api.get("/admin/payments/online"),
      ]);
      const manualRows = (manual.data || []).map((item) => ({
        ...item, type: "Manual", provider: item.transferType || "BANK_TRANSFER",
        id: item.transactionId, created: item.createdAt,
      }));
      const onlineRows = (online.data || []).map((item) => ({
        ...item, type: "Online", id: item.transactionId, created: item.createdAt,
      }));
      const requestRows = (manualRequests.data || []).map((item) => ({
        ...item,
        type: "Manual",
        source: "request",
        provider: item.paymentMethod || "INSTAPAY",
        id: item.requestId,
        studentName: `${item.studentFirstName || ""} ${item.studentLastName || ""}`.trim(),
        amount: item.finalPrice,
        status: item.payed && String(item.status).toUpperCase() === "APPROVED"
          ? "SUCCEEDED"
          : String(item.status || "PENDING").toUpperCase(),
        confirmationEmailSent: item.emailConfirmationSent,
        created: item.requestDate,
      }));
      setRows([...manualRows, ...requestRows, ...onlineRows].sort((a, b) => String(b.created || "").localeCompare(String(a.created || ""))));
    } catch (error) {
      setMessage({ severity: "error", text: error.response?.data?.message || "Could not load payment tracking." });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    setSendingId(row.id);
    try {
      const endpoint = row.source === "request"
        ? `/admin/payments/manual-requests/${row.id}/resend-confirmation`
        : `/admin/payments/${row.id}/resend-confirmation`;
      const response = await api.post(endpoint);
      setMessage({ severity: "success", text: response.data?.message || "Confirmation email sent." });
      await load();
    } catch (error) {
      setMessage({ severity: "error", text: error.response?.data?.message || "Could not send confirmation email." });
    } finally { setSendingId(null); }
  };

  return <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fb" }}>
    <AdminNavbar />
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={2}>
        <Box><Typography variant="h4" fontWeight={800}>Payment Tracking</Typography><Typography color="text.secondary">Manual and online payments, enrollment, and invoice-email delivery.</Typography></Box>
        <Stack direction="row" gap={1}><Button variant="outlined" onClick={() => navigate("/dashboard/payments/bank-transfer")}>Manage manual</Button><Button variant="outlined" onClick={() => navigate("/dashboard/payments/online")}>Manage online</Button><Button onClick={load}>Refresh</Button></Stack>
      </Stack>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Paper sx={{ overflowX: "auto" }}>
        {loading ? <Box textAlign="center" py={8}><CircularProgress /></Box> : <Table size="small">
          <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Type</TableCell><TableCell>Provider</TableCell><TableCell>Student</TableCell><TableCell>Course</TableCell><TableCell>Amount</TableCell><TableCell>Payment</TableCell><TableCell>Invoice email</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
          <TableBody>{rows.map((row) => <TableRow key={`${row.type}-${row.id}`} hover>
            <TableCell>{row.id}</TableCell><TableCell><Chip size="small" label={row.type} /></TableCell><TableCell>{row.provider}</TableCell>
            <TableCell>{row.studentName}<Typography variant="caption" display="block">{row.studentEmail}</Typography></TableCell>
            <TableCell>{row.courseTitle}</TableCell><TableCell>{row.currency} {Number(row.amount || 0).toFixed(2)}</TableCell>
            <TableCell><Chip size="small" color={row.status === "SUCCEEDED" ? "success" : row.status === "FAILED" ? "error" : "warning"} label={row.status} /></TableCell>
            <TableCell><Chip size="small" color={row.confirmationEmailSent ? "success" : "default"} label={row.confirmationEmailSent ? "Sent" : "Not sent"} /></TableCell>
            <TableCell>{row.created ? new Date(row.created).toLocaleString() : "-"}</TableCell>
            <TableCell><Button size="small" disabled={row.status !== "SUCCEEDED" || sendingId === row.id} onClick={() => resend(row)}>{sendingId === row.id ? "Sending..." : "Resend invoice"}</Button></TableCell>
          </TableRow>)}</TableBody>
        </Table>}
      </Paper>
    </Box>
  </Box>;
};
export default AdminPaymentTrackingPage;
